import type { TouchData, TrackingCookie } from '../types/tracking.js';
import { TRACKING_COOKIE_VERSION } from '../config.js';
import { getTrackingData, writeTrackingData } from './tracking-cookie.js';
import { slimUrl, slimReferrer } from '../attribution/url-slim.js';
import { logger } from '../utils/logger.js';

/** Toutes les clés plates du format v1 à nettoyer après migration */
const LEGACY_KEYS = [
  'utm_source_first_touch', 'utm_medium_first_touch', 'utm_campaign_first_touch',
  'utm_term_first_touch', 'utm_content_first_touch',
  'utm_source_last_touch', 'utm_medium_last_touch', 'utm_campaign_last_touch',
  'utm_term_last_touch', 'utm_content_last_touch',
  'website_source', 'website_source_last_touch',
  'referrer_first_touch', 'referrer_last_touch',
  'landing_page_first_touch', 'landing_page_last_touch',
  'timestamp_first_touch', 'timestamp_last_touch',
  'gclid', 'msclkid', 'li_fat_id', 'fbclid',
] as const;

type LegacyKey = (typeof LEGACY_KEYS)[number];

/** Détecte la présence d'au moins une clé plate legacy dans l'objet cookie */
function detectLegacyKeys(data: Record<string, unknown>): boolean {
  return LEGACY_KEYS.some((key) => key in data && data[key] !== undefined);
}

/** Lit une clé legacy comme string, avec fallback vide */
function legacyString(data: Record<string, unknown>, key: LegacyKey): string {
  const value = data[key];
  return typeof value === 'string' ? value : '';
}

/** Reconstruit un first_touch depuis les clés plates v1 */
function buildFirstTouch(legacy: Record<string, unknown>): TouchData {
  return {
    utm_source: legacyString(legacy, 'utm_source_first_touch'),
    utm_medium: legacyString(legacy, 'utm_medium_first_touch'),
    utm_campaign: legacyString(legacy, 'utm_campaign_first_touch'),
    utm_term: legacyString(legacy, 'utm_term_first_touch'),
    utm_content: legacyString(legacy, 'utm_content_first_touch'),
    // Les click IDs legacy étaient à la racine — on ne les associe qu'au last_touch
    gclid: '',
    msclkid: '',
    li_fat_id: '',
    fbclid: '',
    referrer: legacyString(legacy, 'referrer_first_touch'),
    landing_page: legacyString(legacy, 'landing_page_first_touch'),
    website_source: legacyString(legacy, 'website_source'),
    timestamp: legacyString(legacy, 'timestamp_first_touch') || new Date().toISOString(),
  };
}

/** Reconstruit un last_touch depuis les clés plates v1 */
function buildLastTouch(legacy: Record<string, unknown>): TouchData {
  return {
    utm_source: legacyString(legacy, 'utm_source_last_touch'),
    utm_medium: legacyString(legacy, 'utm_medium_last_touch'),
    utm_campaign: legacyString(legacy, 'utm_campaign_last_touch'),
    utm_term: legacyString(legacy, 'utm_term_last_touch'),
    utm_content: legacyString(legacy, 'utm_content_last_touch'),
    gclid: legacyString(legacy, 'gclid'),
    msclkid: legacyString(legacy, 'msclkid'),
    li_fat_id: legacyString(legacy, 'li_fat_id'),
    fbclid: legacyString(legacy, 'fbclid'),
    referrer: legacyString(legacy, 'referrer_last_touch'),
    landing_page: legacyString(legacy, 'landing_page_last_touch'),
    website_source: legacyString(legacy, 'website_source_last_touch') || legacyString(legacy, 'website_source'),
    timestamp: legacyString(legacy, 'timestamp_last_touch') || new Date().toISOString(),
  };
}

/** Supprime toutes les clés legacy d'un objet cookie cloné */
function removeLegacyKeys(data: Record<string, unknown>): Record<string, unknown> {
  const cleaned = { ...data };
  for (const key of LEGACY_KEYS) {
    delete cleaned[key];
  }
  return cleaned;
}

/**
 * Slim d'un referrer déjà stocké, idempotent-safe.
 * Un referrer déjà slimmé est un hostname nu (sans scheme) : NE PAS le re-slimmer
 * (slimReferrer('www.linkedin.com') renverrait '' car new URL échoue).
 */
function slimStoredReferrer(v: string): string {
  if (!v) return '';
  return /^https?:\/\//i.test(v) ? slimReferrer(v) : v;
}

/**
 * Slim d'une landing page déjà stockée, idempotent-safe.
 * Ne slimme que les valeurs qui sont encore des URLs complètes.
 */
function slimStoredLanding(v: string): string {
  if (!v) return '';
  return /^https?:\/\//i.test(v) ? slimUrl(v) : v;
}

/** Réécrit une touche avec landing_page/referrer slimmés (click-ids & autres champs intacts). */
function repairTouch(t: TouchData): TouchData {
  return {
    ...t,
    landing_page: slimStoredLanding(t.landing_page),
    referrer: slimStoredReferrer(t.referrer),
  };
}

/**
 * Orchestrateur unique de migration du cookie taleez_tracking.
 * Fusionne les étapes v1 → v2 (reconstruction des touches depuis les clés plates)
 * et v2 → v3 (slim des URLs déjà stockées + purge de middle_touches) en une
 * seule écriture, exécuté au boot.
 *
 * Idempotent : si le cookie est déjà à la dernière version, early return sans écriture.
 */
export function migrateCookie(): void {
  const data = getTrackingData();
  const legacy = data as unknown as Record<string, unknown>;

  const rawVersion = legacy['version'];
  const version = typeof rawVersion === 'number' ? rawVersion : 1;

  // Déjà à la dernière version → idempotent, on ne touche à rien
  if (version >= TRACKING_COOKIE_VERSION) return;

  let working: Record<string, unknown> = { ...legacy };

  // ── Étape v1 → v2 : reconstruire les touches depuis les clés plates ──
  if (version < 2 && detectLegacyKeys(legacy)) {
    logger.info('Migration cookie v1 → v2 détectée');
    working = removeLegacyKeys(legacy);
    if (!data.first_touch) working['first_touch'] = buildFirstTouch(legacy);
    if (!data.last_touch) working['last_touch'] = buildLastTouch(legacy);
  }

  // ── Étape v2 → v3 : purge middle_touches + slim des URLs déjà stockées ──
  delete working['middle_touches'];
  if (working['first_touch']) {
    working['first_touch'] = repairTouch(working['first_touch'] as TouchData);
  }
  if (working['last_touch']) {
    working['last_touch'] = repairTouch(working['last_touch'] as TouchData);
  }
  const firstPageView = working['firstpage_view'];
  if (typeof firstPageView === 'string') {
    working['firstpage_view'] = slimStoredLanding(firstPageView);
  }

  // Une seule écriture cookie
  working['version'] = TRACKING_COOKIE_VERSION;
  writeTrackingData(working as unknown as TrackingCookie);
  logger.info(`Migration cookie → v${TRACKING_COOKIE_VERSION} terminée`);
}
